import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Award, TrendingUp, TrendingDown, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyResults = () => {
  const { t } = useTranslation();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/student/submissions`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreGradient = (score) => {
    if (score >= 70) return 'from-green-500 to-emerald-500';
    if (score >= 50) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-pink-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  const averageScore = submissions.length > 0
    ? submissions.reduce((sum, sub) => sum + sub.score, 0) / submissions.length
    : 0;

  return (
    <div className="space-y-6" data-testid="my-results">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">{t('myResults')}</h2>
        <p className="text-gray-600 mt-1">
          {submissions.length > 0 ? `${submissions.length} ${t('exams')}` : t('resultsNotApproved')}
        </p>
      </div>

      {/* Summary Stats */}
      {submissions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {t('exams')} {t('myExams')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-cyan-600">{submissions.length}</div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {t('score')} (Average)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-4xl font-bold ${getScoreColor(averageScore)}`}>
                {averageScore.toFixed(1)}%
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Best {t('score')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-green-600">
                {Math.max(...submissions.map(s => s.score)).toFixed(1)}%
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Results List */}
      <div className="space-y-4">
        {submissions.length === 0 ? (
          <Card className="shadow-md">
            <CardContent className="pt-6 text-center text-gray-500">
              {t('noData')}
            </CardContent>
          </Card>
        ) : (
          submissions.map((submission, index) => (
            <Card key={submission.id} className="shadow-lg card-hover border-0" data-testid={`result-card-${index}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${getScoreGradient(submission.score)} flex items-center justify-center`}>
                      <Award className="h-10 w-10 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Exam #{index + 1}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('submittedOn')}: {new Date(submission.submitted_at).toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600">
                        {submission.total_questions} {t('questionsCount')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className={`text-4xl font-bold ${getScoreColor(submission.score)}`}>
                        {submission.score}%
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{t('score')}</p>
                    </div>

                    <Link to={`/student/results/${submission.id}`}>
                      <Button data-testid={`view-details-${index}`} className="bg-gradient-to-r from-cyan-600 to-indigo-600">
                        <Eye className="h-4 w-4 mr-2" />
                        {t('viewDetails')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default MyResults;